/**
 * Shared types and helpers for agent workspace panels (inbox + side tabs).
 */

import { getBrandLocale } from '../../shared/utils/brandCurrency';
import i18n from '../../i18n';

const p = (key, opts) => i18n.t(`modules.shared.aiAgent.panelTypes.${key}`, opts);
const sp = (key, opts) => i18n.t(`modules.shared.aiAgent.sidePanels.${key}`, opts);

export function translateRecordStatusLabel(status) {
  if (status == null || String(status).trim() === '') return '—';
  const value = String(status).toLowerCase();
  const rules = [
    [['draft', 'brouillon'], sp('status_draft')],
    [['paid', 'payé', 'paye'], sp('status_paid')],
    [['partial'], sp('status_partial')],
    [['overdue', 'retard'], sp('status_overdue')],
    [['pending', 'en attente', 'en_attente', 'unpaid'], sp('status_pending')],
  ];
  for (const [keys, label] of rules) {
    if (keys.some((key) => value.includes(key))) return label;
  }
  return String(status);
}

export const INBOX_PRIORITY = {
  HIGH: 'high',
  MEDIUM: 'medium',
};

export function parseAmount(value) {
  const amount = parseFloat(value);
  return Number.isFinite(amount) ? amount : 0;
}

export function parseDate(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatShortDate(value) {
  const date = parseDate(value);
  if (!date) return '—';
  return date.toLocaleDateString(getBrandLocale(), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function pickField(row, fields = []) {
  for (const field of fields) {
    const value = row?.[field];
    if (value != null && String(value).trim() !== '') return value;
  }
  return '';
}

export function buildInboxItem({
  id,
  title,
  subtitle,
  prompt,
  priority = INBOX_PRIORITY.MEDIUM,
  icon = 'alert',
  iconTone = 'orange',
  primaryAction = false,
}) {
  return {
    id: String(id),
    icon,
    iconTone,
    title,
    subtitle,
    priority,
    priorityLabel: priority === INBOX_PRIORITY.HIGH ? p('priority_high') : p('priority_medium'),
    urgencySegments: priority === INBOX_PRIORITY.HIGH ? 5 : 3,
    primaryAction,
    prompt,
  };
}

export function buildListPanelItem({
  id,
  title,
  subtitle,
  prompt,
  statusLabel = '—',
  statusTone = 'slate',
  meta,
}) {
  return {
    id: String(id),
    title,
    subtitle,
    prompt,
    statusLabel,
    statusTone,
    meta,
  };
}

export function buildPromptFromRecord(record, template, fields = {}) {
  const values = {};
  Object.entries(fields).forEach(([key, fieldList]) => {
    values[key] = pickField(record, Array.isArray(fieldList) ? fieldList : [fieldList]);
  });
  return template.replace(/\{(\w+)\}/g, (_, key) => values[key] || '—');
}

export function filterActionable(rows = [], statusFields = ['status']) {
  const pending = ['pending', 'open', 'active', 'draft', 'brouillon', 'en_attente', 'en attente', 'scheduled', 'planned', 'overdue', 'retard', 'urgent'];
  return rows.filter((row) => {
    const status = String(pickField(row, statusFields)).toLowerCase();
    if (!status) return true;
    return pending.some((p) => status.includes(p));
  });
}

export function emptyModulePanels() {
  return {
    panels: {},
    counts: {},
    recentDocuments: [],
    paymentGroups: [],
    paymentCount: 0,
  };
}
